/**
 * Site-wide deferred JS — Phase 35.5 (dealzsaudi 2026-06-22 port of
 * dealzbookae Phase 35.5 Win D, originally shipped 2026-05-15).
 *
 * Consolidates two previously-inline <script> blocks from master.blade.php
 * (the .clckcatalog click handler + native lazy-load enhancement) into a
 * single external file loaded with `defer` and wrapped in
 * requestIdleCallback. This pushes their execution past the browser's
 * initial paint + LCP window, reducing TBT contribution from ~20-45ms
 * to near-zero (idle-time work doesn't count against TBT).
 *
 * What was inlined before Phase 35.5:
 *   1. .clckcatalog click handler — per-element addEventListener pattern,
 *      ran on DOMContentLoaded. Replaced here with event-delegation
 *      pattern (single document-level click listener) — cleaner code AND
 *      works for dynamically loaded catalog cards (e.g. load-more.js
 *      pagination on listing pages).
 *      ALSO fixes a latent bug: the inline handler assigned
 *      `window.location = href` (raw href) instead of the just-computed
 *      `url` (with BASE_URL prefix when href is relative). This handler
 *      uses the processed `url`.
 *   2. Native lazy-load enhancement + image-error fallback — image
 *      error handling, IntersectionObserver fallback for browsers
 *      without native lazy-load support, MutationObserver for
 *      dynamically loaded images.
 *      ALSO fixes a latent bug: the inline handler referenced
 *      thumb_error_image.webp which doesn't exist in public/; this
 *      handler uses thumb_missing.webp (matches dealzbookae + the
 *      actual asset on disk).
 *
 * Loaded from master.blade.php as:
 *   @vite(['resources/js/site.js'])
 *
 * @vite() emits <script type="module"> which is deferred by HTML spec.
 * The requestIdleCallback wrap further pushes EXECUTION past the
 * FCP-to-TTI window.
 *
 * Window globals exposed: NONE. All work is scoped via IIFE.
 *
 * Dependencies pulled from Blade template variables (resolved at Blade
 * render time, NOT runtime):
 *   - {{ url('/') }} for the catalog URL prefix fallback
 *   - {{ asset('thumb_missing.webp') }} for image error placeholder
 *   - {{ asset('thumb_loading_image.webp') }} for lazy-load fallback
 *      placeholder (only used in non-native-lazy-load browsers)
 *
 * Since these need Blade interpolation, the URLs are emitted into
 * window.__siteJsConfig by an inline <script> in master.blade.php
 * BEFORE this file loads.
 *
 * Phase 40 Step 5.3 (2026-06-26): setupDeferredImages() ADDED — IntersectionObserver-
 *   driven swap-in for images marked with data-defer-src (homepage featured-store
 *   logos via partials/logos.blade.php deferLogos=true). Paired with the
 *   Phase 40 Step 5.1 data-defer-src Blade pattern.
 *
 * Intentionally NOT ported from dealzbookae's site.js:
 *   - Phase 40.1 (setupAdSenseScheduler) — requires
 *     config('adsense.slot_activation.defer_enabled') and the
 *     data-adsense-activation attribute on the <x-adsense-slot>
 *     component's <ins>. Neither exists on dealzsaudi. Add when porting
 *     Phase 40.1.
 */
(function () {
    'use strict';

    // ===========================================================================
    // Config from Blade-rendered window globals (see master.blade.php inline init)
    // ===========================================================================
    var config = (window.__siteJsConfig) || {};
    var BASE_URL = config.baseUrl || '';
    var IMG_MISSING = config.imgMissing || '';
    var IMG_LOADING = config.imgLoading || '';

    // ===========================================================================
    // .clckcatalog click handler — event delegation pattern
    // ===========================================================================
    // Previous (inline): per-element addEventListener registered for every
    // .clckcatalog on DOMContentLoaded. Inefficient for many catalog cards;
    // doesn't catch dynamically-loaded cards (e.g. via load-more.js
    // pagination).
    //
    // New pattern: single document-level click listener, walks up to the
    // nearest .clckcatalog ancestor. Zero per-element registration; works
    // for any dynamically-loaded card; consistent with the accordion
    // pattern that already lives inline in master.blade.php.
    function handleCatalogClick(e) {
        var el = e.target.closest('.clckcatalog');
        if (!el) return;
        var infoLink = el.querySelector('.info');
        if (!infoLink) return;
        var href = infoLink.getAttribute('href');
        if (!href) return;
        // Check if href is already an absolute URL (from locale_route()).
        // If it starts with http, use it directly; otherwise prepend base URL.
        var url = (href.indexOf('http') === 0) ? href : BASE_URL + href;
        window.location = url;
    }

    // ===========================================================================
    // Native lazy-load enhancement — image error fallback + IO fallback
    // ===========================================================================
    function handleImageError(img) {
        if (!img.hasAttribute('data-error-handled')) {
            img.setAttribute('data-error-handled', 'true');
            img.src = IMG_MISSING;
            img.alt = 'Image not available';
        }
    }

    function addErrorHandling() {
        var allImages = document.querySelectorAll('img[loading="lazy"]');
        for (var i = 0; i < allImages.length; i++) {
            allImages[i].addEventListener('error', function () {
                handleImageError(this);
            });
        }
    }

    function setupNativeLazyLoadFallback() {
        // Path 1: modern browsers with native loading="lazy" support —
        // just add error handling, no IntersectionObserver needed.
        if ('loading' in HTMLImageElement.prototype) {
            addErrorHandling();
            return;
        }

        // Path 2: older browsers without native lazy-load — use
        // IntersectionObserver to swap placeholder → real src on
        // approach to viewport.
        var images = document.querySelectorAll('img[loading="lazy"]');
        if ('IntersectionObserver' in window) {
            var imageObserver = new IntersectionObserver(function (entries) {
                entries.forEach(function (entry) {
                    if (entry.isIntersecting) {
                        var img = entry.target;
                        if (img.dataset.lazySrc && img.src.indexOf('thumb_loading_image.webp') !== -1) {
                            img.src = img.dataset.lazySrc;
                            img.removeAttribute('data-lazy-src');
                        }
                        imageObserver.unobserve(img);
                    }
                });
            }, { rootMargin: '50px 0px' });

            images.forEach(function (img) {
                if (img.src && img.src.indexOf('thumb_loading_image.webp') === -1) {
                    img.dataset.lazySrc = img.src;
                    img.src = IMG_LOADING;
                }
                imageObserver.observe(img);
            });
        } else {
            // Path 3: very old browsers, no IO support either — load
            // all lazy images immediately.
            images.forEach(function (img) {
                if (img.dataset.lazySrc) {
                    img.src = img.dataset.lazySrc;
                    img.removeAttribute('data-lazy-src');
                }
            });
        }

        addErrorHandling();
    }

    // Re-attach error handling for dynamically-loaded images (e.g. via
    // load-more.js pagination on listing pages).
    function setupMutationObserver() {
        var observer = new MutationObserver(function (mutations) {
            mutations.forEach(function (mutation) {
                if (mutation.type !== 'childList') return;
                mutation.addedNodes.forEach(function (node) {
                    if (node.nodeType !== 1) return; // not an element
                    var newImages = node.querySelectorAll ? node.querySelectorAll('img[loading="lazy"]') : [];
                    newImages.forEach(function (img) {
                        img.addEventListener('error', function () {
                            handleImageError(this);
                        });
                    });
                });
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }

    // ===========================================================================
    // Phase 40 Step 5.3 (2026-06-26 dealzsaudi port) — IntersectionObserver-driven
    // swap-in for images marked with data-defer-src (homepage featured-store
    // logos). Keeps the page from fetching ~25 logos on initial load; defers
    // until they scroll into view. Promotes data-defer-src → src and
    // data-defer-srcset → srcset, then removes the data-* attrs so the swap
    // is one-shot.
    //
    // Caller responsibility: <img> elements opt in by carrying both:
    //   - a placeholder src= (typically a 0-byte transparent data: SVG that
    //     reserves the layout box so CLS stays at 0)
    //   - data-defer-src="<real-url>"
    //   - data-defer-srcset="<real-srcset>" (optional)
    // partials/logos.blade.php uses this pattern when called with
    // deferLogos=true (home.blade.php's include site).
    //
    // Path 1: modern browsers with IntersectionObserver → defer until scroll.
    // Path 2: old browsers (no IO support) → immediate promote-all (eager
    //         fall-through; better than leaving a transparent placeholder
    //         permanently visible).
    //
    // The 200px rootMargin pre-warms the swap so images are visibly ready by
    // the time they're actually in view — no perceived load delay.
    // ===========================================================================
    function setupDeferredImages() {
        var imgs = document.querySelectorAll('img[data-defer-src]');
        if (!imgs.length) return;

        if (!('IntersectionObserver' in window)) {
            // Path 2 — no IO support. Promote all immediately so the page is
            // never stuck on the transparent placeholder.
            imgs.forEach(function (img) {
                var srcset = img.getAttribute('data-defer-srcset');
                var src = img.getAttribute('data-defer-src');
                if (srcset) img.setAttribute('srcset', srcset);
                if (src) img.src = src;
                img.removeAttribute('data-defer-src');
                img.removeAttribute('data-defer-srcset');
            });
            return;
        }

        // Path 1 — IO available. Promote per-image as they approach viewport.
        var observer = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
                if (!entry.isIntersecting) return;
                var img = entry.target;
                var srcset = img.getAttribute('data-defer-srcset');
                var src = img.getAttribute('data-defer-src');
                if (srcset) img.setAttribute('srcset', srcset);
                if (src) img.src = src;
                img.removeAttribute('data-defer-src');
                img.removeAttribute('data-defer-srcset');
                observer.unobserve(img);
            });
        }, { rootMargin: '200px 0px' });

        imgs.forEach(function (img) {
            observer.observe(img);
        });
    }

    // ===========================================================================
    // Init — register all handlers, deferred to browser idle time
    // ===========================================================================
    function initSiteJs() {
        // Event delegation — register once at document level.
        document.addEventListener('click', handleCatalogClick);

        // Native lazy-load enhancement + image error fallback.
        setupNativeLazyLoadFallback();

        // MutationObserver for dynamically-loaded images.
        setupMutationObserver();

        // Phase 40 Step 5.3 (2026-06-26): swap-in deferred (data-defer-src)
        // images on scroll. No-op when no deferred images on page (most pages).
        setupDeferredImages();
    }

    // ===========================================================================
    // Navigation + accordion interactions (moved from inline <script> blocks in
    // navigation.blade / master.blade — Phase 61.3). These bind IMMEDIATELY, not in
    // the idle-deferred init below, so they respond from first paint. All are
    // document-delegated, so binding as this deferred module runs is safe.
    // Locale data comes from <body>: data-locale-prefix ('' Arabic / '/en' English)
    // and data-city-url (the locale_route('cities.show') URL with a CITY_PLACEHOLDER
    // slug), so the JS never hand-builds a locale or route path.
    // ===========================================================================
    function localePrefix() {
        return (document.body && document.body.dataset.localePrefix) || '';
    }

    function cityUrl(slug) {
        var template = (document.body && document.body.dataset.cityUrl) || '';
        return template.replace('CITY_PLACEHOLDER', encodeURIComponent(slug));
    }

    function initNavInteractions() {
        // City selector (desktop nav form + mobile popup form) -> the city page.
        // Matches `.city-selector` alone: Phase 43.3 renamed the select's `form-control`
        // to `dlz-form-control`, so the old `.city-selector.form-control` selector never
        // matched and the city dropdown silently did nothing (fixed in 61.3).
        document.addEventListener('change', function (e) {
            if (e.target.matches('.city-selector') && e.target.value !== '') {
                window.location.href = cityUrl(e.target.value);
            }
        });

        // Search submit -> clean path URL (/search/{term} or /en/search/{term}), never ?q=.
        document.addEventListener('submit', function (e) {
            var form = e.target;
            var searchInput = form.querySelector('input[name="q"]');
            if (!searchInput) return;

            e.preventDefault();

            // Desktop form only: if a city is set without a change event having fired
            // (autofill / programmatic value-set), go to the city page instead.
            if (form.id === 'catalogSearchForm') {
                var cityInput = form.querySelector('.city-input .dlz-form-control');
                if (cityInput && cityInput.value !== '') {
                    window.location.href = cityUrl(cityInput.value);
                    return;
                }
            }

            var query = searchInput.value.trim();
            if (!query) return;

            window.location.href = localePrefix() + '/search/' + encodeURIComponent(query);
        });

        // Search icon -> requestSubmit() so the submit handler above fires
        // (form.submit() would bypass it and do a native GET).
        document.addEventListener('click', function (e) {
            if (e.target.closest('.dlz-input-group-text.bg-white')) {
                var form = document.getElementById('catalogSearchForm');
                if (form) {
                    if (form.requestSubmit) { form.requestSubmit(); } else { form.submit(); }
                }
            }
        });

        // Mobile search popup (partials/nav_search_form): every .toggleFormBtn — the
        // search + map icons and the popup's Cancel button — toggles .form-container.
        document.addEventListener('click', function (e) {
            if (e.target.closest('.toggleFormBtn')) {
                var formContainer = document.querySelector('.form-container');
                if (formContainer) {
                    var isVisible = formContainer.style.display !== 'none' &&
                                    getComputedStyle(formContainer).display !== 'none';
                    formContainer.style.display = isVisible ? 'none' : 'block';
                }
            }
        });

        // Mobile toggles: search form visibility / focus the city selector.
        document.addEventListener('click', function (e) {
            if (e.target.closest('.search-toggle')) {
                var searchForm = document.querySelector('.inline-search');
                if (searchForm) {
                    var isHidden = searchForm.style.display === 'none' ||
                                   getComputedStyle(searchForm).display === 'none';
                    searchForm.style.display = isHidden ? 'block' : 'none';
                }
            } else if (e.target.closest('.map-toggle')) {
                var citySelector = document.querySelector('.city-selector');
                if (citySelector) {
                    citySelector.focus();
                    if (citySelector.size <= 1) {
                        citySelector.size = Math.min(citySelector.options.length, 5);
                        setTimeout(function () { citySelector.size = 1; }, 3000);
                    }
                }
            }
        });

        // Language dropdown: open state lives in inline style.display (not a class).
        document.addEventListener('click', function (e) {
            if (e.target.closest('.dlz-dropdown-item')) {
                return; // let the language link work normally
            }

            var dropdownToggle = e.target.closest('.locale-btn') || e.target.closest('.dlz-dropdown-toggle');
            if (dropdownToggle) {
                e.preventDefault();
                var parentDropdown = dropdownToggle.closest('.dlz-dropdown') || dropdownToggle.closest('.lang-item');
                var dropdownMenu = parentDropdown ? parentDropdown.querySelector('.dlz-dropdown-menu') : null;

                if (dropdownMenu) {
                    document.querySelectorAll('.dlz-dropdown-menu').forEach(function (menu) {
                        if (menu !== dropdownMenu) menu.style.display = 'none';
                    });
                    dropdownMenu.style.display = dropdownMenu.style.display === 'block' ? 'none' : 'block';
                }
            } else {
                // Clicked outside: hide all dropdowns.
                document.querySelectorAll('.dlz-dropdown-menu').forEach(function (menu) {
                    menu.style.display = 'none';
                });
            }
        });
    }

    // Accordion / collapse (data-dlz-toggle="collapse"; no Bootstrap JS).
    // Covers: faq, catalog/show, menu/show, other_offer/show, store/show.
    function initCollapse() {
        document.addEventListener('click', function (e) {
            var btn = e.target.closest('[data-dlz-toggle="collapse"]');
            if (!btn) return;
            e.preventDefault();

            var targetSelector = btn.getAttribute('data-dlz-target');
            if (!targetSelector) return;
            var targetPanel = document.querySelector(targetSelector);
            if (!targetPanel) return;

            var parentSelector = targetPanel.getAttribute('data-dlz-parent');
            var isCurrentlyOpen = targetPanel.classList.contains('dlz-open');

            // Accordion: close sibling panels first.
            if (parentSelector) {
                var parentEl = document.querySelector(parentSelector);
                if (parentEl) {
                    parentEl.querySelectorAll('.dlz-collapse.dlz-open').forEach(function (openPanel) {
                        if (openPanel !== targetPanel) {
                            openPanel.classList.remove('dlz-open');
                            var siblingBtn = document.querySelector('[data-dlz-target="#' + openPanel.id + '"]');
                            if (siblingBtn) { siblingBtn.classList.add('dlz-collapsed'); siblingBtn.setAttribute('aria-expanded', 'false'); }
                        }
                    });
                }
            }

            if (isCurrentlyOpen) {
                targetPanel.classList.remove('dlz-open');
                btn.classList.add('dlz-collapsed');
                btn.setAttribute('aria-expanded', 'false');
            } else {
                targetPanel.classList.add('dlz-open');
                btn.classList.remove('dlz-collapsed');
                btn.setAttribute('aria-expanded', 'true');
            }
        });
    }

    // Interactive nav + accordion bind now (must work from first paint), not at idle.
    initNavInteractions();
    initCollapse();

    // Defer init past initial paint via requestIdleCallback (Phase 35.5
    // Win D pattern). @vite() emits <script type="module"> which is
    // already deferred per HTML spec; this further pushes EXECUTION to
    // idle time. Fallback to setTimeout for Safari < 16.4 (no
    // requestIdleCallback).
    if ('requestIdleCallback' in window) {
        requestIdleCallback(initSiteJs, { timeout: 2000 });
    } else {
        setTimeout(initSiteJs, 100);
    }
})();
